import { useState } from 'react';
import { ClipLoader } from 'react-spinners';
import { format } from 'date-fns';
import useApi from '../../../../../../utils/AxiosWrapper';
import { useUser } from '../../../../../../utils/UserContext';
import { useScreenView } from '../../../../../../utils/ScreenViewContext';
import { useQuery } from '@tanstack/react-query';
import {
  CheckCircle,
  Clock,
  PaperPlaneTilt,
  UserCheck,
} from '@phosphor-icons/react';

interface ReferredCustomer {
  email: string;
  legalBusinessName: string | null;
  status: 'sent' | 'pending' | 'approved';
  createdAt?: string;
  customerId?: string;
  recipientName?: string | null;
  isSentInvite?: boolean;
}

interface ReferralInvitesListProps {
  userType: string;
}

const ReferralInvitesList = ({ userType }: ReferralInvitesListProps) => {
  const { screenView } = useScreenView();
  const { callProtectedApi } = useApi();
  const { userId } = useUser();
  const [selectedFilter, setSelectedFilter] = useState<string>('all');

  const { data: customers = [], isLoading } = useQuery<ReferredCustomer[]>({
    queryKey: ['referredCustomers', userId, userType],
    queryFn: async () => {
      const response = await callProtectedApi(
        `/referrals/customers/${userType}/${userId}`,
        'get',
        {}
      );

      // Try different paths to find the array
      const result =
        response?.data?.data?.referredCustomers ||
        response?.data?.referredCustomers ||
        response?.data?.data ||
        response?.data ||
        [];

      return result;
    },
    enabled: !!userId && !!userType,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sent':
        return (
          <PaperPlaneTilt size={20} className="text-blue-600" weight="fill" />
        );
      case 'pending':
        return (
          <UserCheck size={20} className="text-purple-600" weight="fill" />
        );
      case 'approved':
        return (
          <CheckCircle size={20} className="text-green-600" weight="fill" />
        );
      default:
        return <Clock size={20} className="text-gray-600" weight="fill" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      sent: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Sent' },
      pending: {
        bg: 'bg-purple-100',
        text: 'text-purple-800',
        label: 'Signed Up',
      },
      approved: {
        bg: 'bg-green-100',
        text: 'text-green-800',
        label: 'Approved',
      },
    };

    const config =
      statusConfig[status as keyof typeof statusConfig] || statusConfig.sent;

    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${config.bg} ${config.text}`}
      >
        {getStatusIcon(status)}
        {config.label}
      </span>
    );
  };

  // Ensure customers is always an array
  const customersArray = Array.isArray(customers) ? customers : [];

  const filteredCustomers = customersArray.filter((customer) => {
    if (selectedFilter === 'all') return true;
    if (selectedFilter === 'sent') return customer.status === 'sent';
    if (selectedFilter === 'signed_up') return customer.status === 'pending';
    if (selectedFilter === 'approved') return customer.status === 'approved';
    return false;
  });

  const statusCounts = {
    all: customersArray.length,
    sent: customersArray.filter((c) => c.status === 'sent').length,
    signed_up: customersArray.filter((c) => c.status === 'pending').length,
    approved: customersArray.filter((c) => c.status === 'approved').length,
  };

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <div className="w-full rounded-lg border-[1px] border-bordergray bg-white p-6">
            <div className="mb-6">
              <h2 className="mb-2 text-xl font-semibold text-primary">
                Referral Invites
              </h2>
              <p className="text-sm text-gray-600">
                Track all referral invitations sent by your team
              </p>
            </div>

            {/* Filter Tabs */}
            <div className="mb-6 border-b border-gray-200">
              <nav className="-mb-px flex space-x-6">
                {['all', 'sent', 'signed_up', 'approved'].map((status) => (
                  <button
                    key={status}
                    onClick={() => setSelectedFilter(status)}
                    className={`whitespace-nowrap border-b-2 px-1 py-2 text-sm font-medium transition-colors ${
                      selectedFilter === status
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                    } `}
                  >
                    {status.charAt(0).toUpperCase() +
                      status.slice(1).replace('_', ' ')}
                    <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs">
                      {statusCounts[status as keyof typeof statusCounts]}
                    </span>
                  </button>
                ))}
              </nav>
            </div>

            {/* Table */}
            {isLoading ? (
              <div className="flex justify-center py-12">
                <ClipLoader
                  color="#1D213B"
                  loading={isLoading}
                  size={40}
                  aria-label="Loading Spinner"
                />
              </div>
            ) : filteredCustomers.length === 0 ? (
              <div className="py-12 text-center">
                <p className="text-gray-500">No referred customers found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                        Customer
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCustomers.map((customer, index) => (
                      <tr
                        key={`${customer.email}-${index}`}
                        className="border-b border-gray-100 hover:bg-gray-50"
                      >
                        <td className="px-4 py-4">
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {customer.isSentInvite
                                ? customer.recipientName || 'N/A'
                                : customer.legalBusinessName || 'N/A'}
                            </p>
                            <p className="text-xs text-gray-500">
                              {customer.email}
                            </p>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          {getStatusBadge(customer.status)}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600">
                          {customer.createdAt
                            ? format(
                                new Date(customer.createdAt),
                                'MMM d, yyyy'
                              )
                            : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Summary Stats */}
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="rounded-lg bg-blue-50 p-4">
                <p className="text-sm font-medium text-blue-600">
                  Total Referred Customers
                </p>
                <p className="text-2xl font-bold text-blue-900">
                  {statusCounts.all}
                </p>
              </div>
              <div className="rounded-lg bg-green-50 p-4">
                <p className="text-sm font-medium text-green-600">
                  Approved Referrals
                </p>
                <p className="text-2xl font-bold text-green-900">
                  {statusCounts.approved}
                </p>
              </div>
              <div className="rounded-lg bg-purple-50 p-4">
                <p className="text-sm font-medium text-purple-600">
                  Approval Rate
                </p>
                <p className="text-2xl font-bold text-purple-900">
                  {statusCounts.all > 0
                    ? `${((statusCounts.approved / statusCounts.all) * 100).toFixed(1)}%`
                    : '0%'}
                </p>
              </div>
            </div>
          </div>
        );
    }
  };

  return <>{renderScreenView(screenView)}</>;
};

export default ReferralInvitesList;
